import React, { useEffect, useState } from "react";
import { Box, Loader } from "@mantine/core";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend, TooltipItem } from "chart.js";

// Register chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export interface Transaction {
  date: {
    toDate: () => Date;
  };
  creditQuantity: number;
}

export interface CreditConsumptionChartProps {
  processedTransactions: Transaction[] | null | undefined;
}

export interface ChartDataset {
  label: string;
  data: number[];
  backgroundColor: string;
}

export interface ChartDataStructure {
  labels: string[];
  datasets: ChartDataset[];
}

const CreditConsumptionChart = ({ processedTransactions }: CreditConsumptionChartProps) => {
  const [chartData, setChartData] = useState<ChartDataStructure | null>(null);

  const calculateWeeklyTotals = (transactions: Transaction[]) => {
    if (!transactions || transactions.length === 0) return;

    const weeklyTotals: Record<string, number> = {};

    transactions.forEach((transaction) => {
      const date = transaction.date.toDate(); // Convert Firestore Timestamp to JS Date
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Get the start of the week (Sunday)
      weekStart.setHours(0, 0, 0, 0); // Reset time to midnight

      const weekKey = weekStart.toISOString().split("T")[0]; // Use ISO date string as the key

      if (!weeklyTotals[weekKey]) {
        weeklyTotals[weekKey] = 0;
      }
      weeklyTotals[weekKey] += Math.abs(transaction.creditQuantity); // Use absolute value
    });

    // Prepare data for the chart
    const labels = Object.keys(weeklyTotals)
      .sort() // Sort weeks chronologically
      .slice(-8); // Show only the last 8 weeks

    const data = labels.map((week) => weeklyTotals[week]);

    setChartData({
      labels,
      datasets: [
        {
          label: "Credits Used",
          data,
          backgroundColor: "#434872",
        },
      ],
    });
  };

  useEffect(() => {
    if (processedTransactions) {
      calculateWeeklyTotals(processedTransactions);
    }
  }, [processedTransactions]);

  if (!processedTransactions || processedTransactions.length === 0) {
    return <></>;
  }

  if (!chartData) {
    return <Loader style={{ margin: "1em auto", width: "100%" }} />;
  }

  return (
    <Box
      style={{
        width: "100%",
        maxWidth: "600px",
        height: "500px",
        overflow: "hidden", // Ensure content doesn't overflow
      }}
    >
      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context: TooltipItem<"bar">) {
                  const credits = (context.raw as number) || 0; // Get the credit value
                  const dollars = (Math.ceil(credits * 0.001 * 100) / 100).toFixed(2); // Calculate dollar value
                  const rands = (Math.ceil(credits * 0.02 * 100) / 100).toFixed(2); // Calculate rand value
                  return [
                    `Credits: ${credits}`,
                    `USD: $${dollars}`,
                    `ZAR: ~R${rands}`,
                  ];
                },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        }}
      />
    </Box>
  );
};

export default CreditConsumptionChart;